import { defineStore } from 'pinia'
import type {
  Base,
  FieldingApproach,
  FieldingExecution,
  FieldingTransferQuality,
  FieldingThrowQuality,
  FieldingDecision,
  DefenseSubReason,
  BaserunningJump,
  BaserunningDecision,
  RunnerAdvanceReason,
  ScoutingTagDef,
} from './base-types'
import { useAuthStore } from 'stores/auth-store'
// A rich rule object that links a UI label to its physical database consequences
export interface PlayRule {
  label: string
  value: string
  color?: string
  startBase: Base
  endBase: Base
  isOut: boolean
  runsScored: number
  isError?: boolean
}
export interface ActionButtonDef {
  label: string
  value: string
  color: string
  textColor?: string
  visible?: boolean // Allows hiding options dynamically based on user preferences
  tooltip?: string // show tooltip text or not
  icon?: string
  shortCode?: string
}

// module-level helper, above defineStore — not store state, so no `this` ambiguity in getters
function resolveFieldingGroup(position: number, role: string): string {
  if (role === 'relay') return 'relay'
  if (position === 2) return 'catcher'
  if (position === 1) return 'pitcher'
  if (position === 3) return 'firstbase'
  if ([7, 8, 9].includes(position)) return 'outfield'
  return 'infield'
}
// Fine-grained: used by Approach and Execution, where ground-vs-air genuinely changes the vocabulary
function resolveFieldingSituation(position: number, role: string, trajectory?: string): string {
  if (role === 'relay') return 'relay_decision'
  if (role === 'received') return 'receiving'
  // Catcher blocking a pitch is genuinely unlike anything else — left untouched per your call
  if (position === 2 && role === 'fielded') return 'catcher_blocking'
  if (role === 'fielded') {
    const isAirBall = trajectory ? ['F', 'L', 'P'].includes(trajectory) : false
    return isAirBall ? 'air_ball' : 'ground_ball'
  }
  return 'ground_ball' // safe fallback
}

// Coarse: used by Throw and Decision, where ground/air doesn't change anything —
// only "did they field it, receive it, or relay it" matters
function resolveThrowDecisionGroup(position: number, role: string): string {
  if (role === 'relay') return 'relay'
  if (role === 'received') return 'receiving'
  if (position === 2 && role === 'fielded') return 'catcher'
  return 'fielding'
}

function formatGradedOption(baseLabel: string, weight: number) {
  let suffix = ''
  let color = 'default' // Fallback color

  if (weight >= 2) {
    suffix = ' ++'
    color = 'positive' // Quasar green
  } else if (weight === 1) {
    suffix = ' +'
    color = 'positive'
  } else if (weight === -1) {
    suffix = ' -'
    color = 'warning' // Quasar orange/yellow for minor misses
  } else if (weight <= -2) {
    suffix = ' --'
    color = 'negative' // Quasar red for major misses
  }

  return {
    label: `${baseLabel}${suffix}`,
    color,
    weight,
  }
}

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    // Store your master arrays as "raw" private variables
    _rawPitchAdvancedOptions: [
      { label: 'Dropped 3rd Strike', value: 'D3' },
      { label: 'Intentional Ball', value: 'I' },
      { label: 'No Pitch (Interference/Balk)', value: 'N' },
      { label: 'Foul Tip', value: 'T' },
      { label: 'Foul Bunt', value: 'L' },
      { label: 'Missed Bunt Attempt', value: 'M' },
      { label: 'Unknown or Missed Pitch', value: 'U' },
      { label: 'Called Ball (Timer/Mouth)', value: 'V' },
      { label: 'Automatic Strike (Timer)', value: 'A' },
    ],

    _rawRunnerAdvanceOptions: [
      { label: 'Stolen Base', value: 'SB', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Wild Pitch', value: 'WP', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Passed Ball', value: 'PB', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Error', value: 'E', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Other', value: 'OA', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Adv on Throw', value: 'TH', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Def Indiff', value: 'DI', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Balk', value: 'BK', default_color: 'grey-3', select_color: 'primary' },
      { label: 'Caught Steal', value: 'CS', default_color: 'red-1', select_color: 'negative' },
      { label: 'Pickoff', value: 'PO', default_color: 'red-1', select_color: 'negative' },
      { label: 'Pickoff CS', value: 'POCS', default_color: 'red-1', select_color: 'negative' },
    ],

    // --- SCOUTING & EVALUATION ---
    _rawScoutingTags: [
      { label: 'Prep Step', value: 'tag_1', category: 'neutral', color: 'info' },
      { label: 'Hustle', value: 'tag_2', category: 'neutral', color: 'info' },
      { label: 'Backup', value: 'tag_3', category: 'neutral', color: 'info' },
      { label: 'Good Comm.', value: 'tag_4', category: 'positive', color: 'positive' },
      { label: 'Custom Note', value: 'tag_5', category: 'neutral', color: 'info' },
    ] as ScoutingTagDef[],

    // --- PITCHING ACTION UI CONFIGURATIONS ---
    // --- DATA DRIVEN PITCH CONTROLS ---
    primaryPitchCategories: [
      { label: 'Ball', value: 'B', color: 'positive', visible: true },
      { label: 'Strike', value: 'S', color: 'negative', visible: true },
      { label: 'In Play', value: 'X', color: 'info', visible: true },
    ] as ActionButtonDef[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    subPitchCategories: {
      B: [
        { label: 'Wild Pitch', value: 'W', color: 'positive', visible: true },
        { label: 'HBP', value: 'H', color: 'positive', visible: true },
      ],
      S: [
        { label: 'Called', value: 'C', color: 'negative', visible: true },
        { label: 'Swinging', value: 'S', color: 'negative', visible: true },
        { label: 'Foul', value: 'F', color: 'warning', textColor: 'black', visible: true },
      ],
    } as Record<string, ActionButtonDef[]>,
    pitchTypes: [
      { label: 'Fastball', value: 'FASTBALL', color: 'secondary', visible: true },
      { label: 'Changeup', value: 'CHANGEUP', color: 'secondary', visible: true },
    ] as ActionButtonDef[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    pitchResultColors: {
      B: '#4CAF50', // Green
      W: '#9C27B0', // Purple
      H: '#00796B', // Dark Green
      C: '#F44336', // Red
      S: '#B71C1C', // Dark Red
      F: '#FF9800', // Orange
      X: '#2196F3', // Blue
      K: '#C10015', //negative
    } as Record<string, string>,

    // --- FIELDING OPTIONS ---
    allFieldingLabels: [
      { label: 'Out', value: 'OUT', shortCode: 'O' },
      { label: 'Out', value: 'OUT_Q', shortCode: 'O' },
      { label: 'FC', value: 'FC', shortCode: 'FC' },
      { label: '1B', value: 'S', shortCode: 'S' },
      { label: '2B', value: 'D', shortCode: 'D' },
      { label: '3B', value: 'T', shortCode: 'T' },
      { label: 'HR', value: 'HR', shortCode: 'HR' },
    ],

    primaryFieldingOutcomes: [
      { label: 'Out', value: 'OUT', color: 'negative', visible: true },
      { label: '1B', value: 'S', color: 'positive', visible: true },
      { label: '2B', value: 'D', color: 'positive', visible: true },
      { label: '3B', value: 'T', color: 'positive', visible: true },
    ] as ActionButtonDef[],

    primaryFieldingTrajectories: [
      {
        label: 'Grnd',
        value: 'G',
        color: 'secondary',
        visible: true,
        tooltip: 'Ground ball',
        icon: 'south',
      },
      {
        label: 'Line',
        value: 'L',
        color: 'secondary',
        visible: true,
        tooltip: 'Line drive',
        icon: 'trending_flat',
      },
      {
        label: 'Fly',
        value: 'F',
        color: 'secondary',
        visible: true,
        tooltip: 'Fly ball',
        icon: 'north',
      },
      {
        label: 'Pop',
        value: 'P',
        color: 'secondary',
        visible: true,
        tooltip: 'Pop fly',
        icon: 'north_east',
      },
    ] as ActionButtonDef[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    fielderZoneScale: {
      1: 0.04, // Pitcher
      2: 0.04, // Catcher have a tight range
      3: 0.05, // Corner Infielders guard the foul lines closely
      4: 0.07, // Middle Infielders bridge the dirt gaps
      5: 0.05, // Corner Infielders guard the foul lines closely
      6: 0.07, // Middle Infielders bridge the dirt gaps
      7: 0.1, // Outfielders cover the deep grass ~25% of the total field radius
      8: 0.1,
      9: 0.1,
    } as Record<number, number>,

    // --- ADVANCED PLAY OPTIONS (For Drawer) ---
    playAllOutcomes: [
      //{ label: 'Throw', value: 'TH' },
      //{ label: "Fielder's Choice", value: 'FC' },
      { label: 'Out (1)', value: 'OUT', tooltip: '1 Out' },
      { label: 'FC (1)', value: 'FC', tooltip: 'Fielders Choice 1 Out' },
      { label: '1B', value: 'S', tooltip: 'Single' },
      { label: '2B', value: 'D', tooltip: 'Double' },
      { label: '3B', value: 'T', tooltip: 'Triple' },
      { label: 'HR', value: 'HR', tooltip: 'Home Run' },
      { label: 'Double Play (2)', value: 'DP', tooltip: 'Double Play 2 Outs' },
      { label: 'Triple Play (3)', value: 'TP', tooltip: 'Triple Play 3 Outs' },
    ],

    primaryPlayOutcomes: [
      { label: 'Out (1)', value: 'OUT', color: 'negative', visible: true, tooltip: '1 Out' },
      {
        label: 'FC (1)',
        value: 'FC',
        color: 'negative',
        visible: true,
        tooltip: '1 Out Fielders Choice',
      },
      { label: '1B', value: 'S', color: 'positive', visible: true, tooltip: 'Single' },
    ] as ActionButtonDef[],

    playAdvancedOutcomes: [
      { label: '2B', value: 'D', color: 'positive', visible: true, tooltip: 'Double' },
      { label: '3B', value: 'T', color: 'positive', visible: true, tooltip: 'Triple' },
      { label: 'HR', value: 'HR', color: 'positive', visible: true, tooltip: 'Home Run' },
      {
        label: 'Double Play (2)',
        value: 'DP',
        color: 'negative',
        visible: true,
        tooltip: 'Double Play 2 Outs',
      },
      {
        label: 'Triple Play (3)',
        value: 'TP',
        color: 'negative',
        visible: true,
        tooltip: 'Triple Play 3 Outs',
      },
    ] as ActionButtonDef[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    positionStrings: {
      1: 'P',
      2: 'C',
      3: '1B',
      4: '2B',
      5: '3B',
      6: 'SS',
      7: 'LF',
      8: 'CF',
      9: 'RF',
    } as Record<number, string>,

    // --- FIELDING ACTION UI CONFIGURATIONS & ADVANCEMENT RULES ---
    primaryFielderRoles: [
      { label: 'Fielded', value: 'fielded' },
      { label: 'Missed Play', value: 'missed_play' },
      { label: 'Relay / Cutoff', value: 'relay' },
      { label: 'Received', value: 'received' },
    ],

    primaryApproachOptionsGroundBall: [
      { value: 'routine', label: 'Routine' },
      { value: 'backhand', label: 'Backhand' },
      { value: 'forehand', label: 'Forehand' },
      { value: 'charge', label: 'Charge (slow roller)' },
      { value: 'dive', label: 'Dive' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsAirBall: [
      { value: 'routine', label: 'Routine' },
      { value: 'ranging_back', label: 'Ranging Back' },
      { value: 'coming_in', label: 'Coming In' },
      { value: 'dive', label: 'Dive' },
      { value: 'wall', label: 'Wall / Track' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsReceiving: [
      { value: 'routine', label: 'Routine' },
      { value: 'scoop', label: 'Scoop (Short Hop)' },
      { value: 'stretch', label: 'Stretch' },
      { value: 'off_bag', label: 'Off-bag Reach' },
      { value: 'adjusting', label: 'Adjusting to Throw' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsRelayDecision: [
      { value: 'routine', label: 'Routine' },
      { value: 'redirect', label: 'Redirect / Cut' },
      { value: 'bad_feed', label: 'Compensating for Bad Feed' },
    ] as { value: FieldingApproach; label: string }[],

    // UNCHANGED — catcher blocking stays exactly as it was
    primaryApproachOptionsCatcher: [
      { value: 'routine', label: 'Routine' },
      { value: 'block_center', label: 'Blocked (Center)' },
      { value: 'block_side', label: 'Blocked (Side)' },
      { value: 'dive', label: 'Dive' },
    ] as { value: FieldingApproach; label: string }[],


    // REMOVE: primaryApproachOptionsInfield, primaryApproachOptionsOutfield,
    // primaryApproachOptionsPitcher, primaryApproachOptionsFirstBase, primaryApproachOptionsRelay

    // Range/Approach
    primaryApproachOptionsInfield: [
      { value: 'routine', label: 'Routine' },
      { value: 'backhand', label: 'Backhand' },
      { value: 'forehand', label: 'Forehand' },
      { value: 'charge', label: 'Charge' },
      { value: 'dive', label: 'Dive' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsOutfield: [
      { value: 'routine', label: 'Routine' },
      { value: 'ranging_back', label: 'Ranging Back' },
      { value: 'coming_in', label: 'Coming In' },
      { value: 'dive', label: 'Dive' },
      { value: 'wall', label: 'Wall / Track' },
    ] as { value: FieldingApproach; label: string }[],


    primaryApproachOptionsPitcher: [
      { value: 'routine', label: 'Routine' },
      { value: 'comebacker', label: 'Comebacker' },
      { value: 'off_the_mound', label: 'Off the Mound' },
      { value: 'cover_base', label: 'Covering Base' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsFirstBase: [
      { value: 'routine', label: 'Routine' },
      { value: 'scoop', label: 'Scoop (Short Hop)' },
      { value: 'stretch', label: 'Stretch' },
      { value: 'off_bag', label: 'Off-bag Reach' },
    ] as { value: FieldingApproach; label: string }[],

    primaryApproachOptionsRelay: [
      { value: 'routine', label: 'Routine' },
      { value: 'adjusting', label: 'Adjusting to Throw' },
      { value: 'redirect', label: 'Redirect / Cut' },
      { value: 'bad_feed', label: 'Bad Incoming Feed' },
    ] as { value: FieldingApproach; label: string }[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    approachWeightClasses: {
      not_scored: 'unscored',
      routine: 'routine',
      stretch: 'routine', // bread-and-butter 1B technique — not extra effort
      cover_base: 'routine', // standard responsibility, not a range play
      block_center: 'routine',

      backhand: 'lateral',
      forehand: 'lateral',
      ranging_back: 'lateral',
      coming_in: 'lateral',
      block_side: 'lateral',
      scoop: 'lateral', // real skill, but comparable difficulty to a backhand pick
      adjusting: 'lateral',
      off_the_mound: 'lateral', // moving to field a bunt — deliberate, not reactive-extreme

      charge: 'aggressive',
      off_bag: 'aggressive',
      redirect: 'aggressive',
      bad_feed: 'aggressive', // relay man compensating for a bad feed — extra difficulty, not their fault

      dive: 'extreme',
      wall: 'extreme',
      comebacker: 'extreme', // near-zero reaction time
    } as Record<string, string>,

    // fielding execution
    primaryExecutionOptionsGroundBall: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'bad_hop', label: 'Bad Hop' },
      { value: 'drop', label: 'Drop' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsAirBall: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'off_glove', label: 'Off Glove' },
      { value: 'drop', label: 'Drop' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsReceiving: [
      { value: 'clean', label: 'Clean' },
      { value: 'short_hop_miss', label: 'Missed Pick' },
      { value: 'pulled_off_bag', label: 'Pulled Off Bag' },
      { value: 'drop', label: 'Drop' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsRelayDecision: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],

// UNCHANGED
    primaryExecutionOptionsCatcher: [
      { value: 'clean', label: 'Clean' },
      { value: 'deflected', label: 'Deflected' },
      { value: 'passed', label: 'Passed Ball' },
      { value: 'dropped_third', label: 'Dropped 3rd Strike' },
      { value: 'missed_tag', label: 'Missed Tag' },
    ] as { value: FieldingExecution; label: string }[],
// REMOVE: primaryExecutionOptionsInfield, primaryExecutionOptionsOutfield,
// primaryExecutionOptionsPitcher, primaryExecutionOptionsFirstBase, primaryExecutionOptionsRelay

    primaryExecutionOptionsInfield: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'bad_hop', label: 'Bad Hop' },
      { value: 'drop', label: 'Drop' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsOutfield: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'off_glove', label: 'Off Glove' },
      { value: 'drop', label: 'Drop' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],


    primaryExecutionOptionsPitcher: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'missed_cover', label: 'Missed Cover' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsFirstBase: [
      { value: 'clean', label: 'Clean' },
      { value: 'short_hop_miss', label: 'Missed Pick' },
      { value: 'pulled_off_bag', label: 'Pulled Off Bag' },
      { value: 'drop', label: 'Drop' },
    ] as { value: FieldingExecution; label: string }[],

    primaryExecutionOptionsRelay: [
      { value: 'clean', label: 'Clean' },
      { value: 'bobble', label: 'Bobble' },
      { value: 'miss', label: 'Miss' },
    ] as { value: FieldingExecution; label: string }[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    executionWeightClasses: {
      not_scored: 'unscored',
      clean: 'clean',
      bad_hop: 'external', // unpredictable bounce, not the fielder's fault

      bobble: 'minor_miss',
      off_glove: 'minor_miss',
      deflected: 'minor_miss',
      pulled_off_bag: 'minor_miss', // technique error, but often still results in the out

      drop: 'major_miss',
      miss: 'major_miss',
      passed: 'major_miss',
      dropped_third: 'major_miss',
      missed_tag: 'major_miss',
      missed_cover: 'major_miss', // routine play converted to no out — same severity as a drop
      short_hop_miss: 'major_miss',
    } as Record<string, string>,

    // Transfer (glove-to-hand exchange before a throw)
    primaryTransferOptionsInfield: [
      { value: 'not_required', label: 'No Transfer' },
      { value: 'clean', label: 'Clean' },
      { value: 'quick', label: 'Quick' },
      { value: 'fumbled', label: 'Fumbled' },
      { value: 'double_clutch', label: 'Double Clutch' },
    ] as { value: FieldingTransferQuality; label: string }[],

    primaryTransferOptionsOutfield: [
      { value: 'not_required', label: 'No Transfer' },
      { value: 'clean', label: 'Clean' },
      { value: 'quick', label: 'Quick' },
      { value: 'fumbled', label: 'Fumbled' },
    ] as { value: FieldingTransferQuality; label: string }[],

    primaryTransferOptionsCatcher: [
      { value: 'not_required', label: 'No Transfer' },
      { value: 'clean', label: 'Clean' },
      { value: 'quick', label: 'Quick' },
      { value: 'fumbled', label: 'Fumbled' },
      { value: 'footwork', label: 'Footwork Issue' },
    ] as { value: FieldingTransferQuality; label: string }[],

    primaryTransferOptionsPitcher: [
      { value: 'not_required', label: 'No Transfer' },
      { value: 'clean', label: 'Clean' },
      { value: 'quick', label: 'Quick' },
      { value: 'fumbled', label: 'Fumbled' },
      { value: 'bare_hand', label: 'Bare-hand' },
    ] as { value: FieldingTransferQuality; label: string }[],
    primaryTransferOptionsRelay: [
      { value: 'not_required', label: 'No Transfer' },
      { value: 'clean', label: 'Clean' },
      { value: 'quick', label: 'Quick' },
      { value: 'fumbled', label: 'Fumbled' },
      { value: 'double_clutch', label: 'Double Clutch' },
    ] as { value: FieldingTransferQuality; label: string }[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    transferWeightClasses: {
      not_required: 'na',
      clean: 'clean',
      quick: 'clean', // rewards speed, but same grade tier as clean — treat as a style flag, not a separate score
      bare_hand: 'clean', // a successful bare-hand transfer is a positive technical event, not a miss
      fumbled: 'minor_miss',
      double_clutch: 'minor_miss',
      footwork: 'minor_miss',
    } as Record<string, string>,

    primaryThrowOptionsFielding: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'cutoff_hit', label: 'Hit Cutoff' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'weak', label: 'Weak / Short' },
      { value: 'missed', label: 'Missed Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsReceiving: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsInfield: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'in_dirt', label: 'In the Dirt' },
      { value: 'missed', label: 'Missed Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsOutfield: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'cutoff_hit', label: 'Hit Cutoff' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'weak', label: 'Weak / Short' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsCatcher: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'in_dirt', label: 'In the Dirt' },
      { value: 'late', label: 'Late Release' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsPitcher: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'missed', label: 'Missed Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsFirstBase: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    primaryThrowOptionsRelay: [
      { value: 'not_required', label: 'No Throw' },
      { value: 'target', label: 'On Target' },
      { value: 'off_target', label: 'Off Target' },
      { value: 'missed', label: 'Missed Target' },
    ] as { value: FieldingThrowQuality; label: string }[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    throwWeightClasses: {
      not_required: 'na',
      target: 'clean',
      cutoff_hit: 'clean', // correct read to hit the cutoff man is good execution, not a miss
      in_dirt: 'minor_miss', // catcher/first baseman can often still pick it — recoverable
      off_target: 'minor_miss',
      late: 'minor_miss',
      weak: 'minor_miss',
      missed: 'major_miss',
    } as Record<string, string>,

    primaryDecisionOptionsGeneral: [
      { value: 'not_required', label: 'No Decision' },
      { value: 'correct', label: 'Correct Choice' },
      { value: 'aggressive', label: 'Aggressive (Reasonable)' },
      { value: 'wrong_base', label: 'Wrong Base' },
      { value: 'forced', label: 'Forced / Rushed' },
    ] as { value: FieldingDecision; label: string }[],
    // used by: infield, outfield, first base, relay

    primaryDecisionOptionsCatcher: [
      { value: 'not_required', label: 'No Decision' },
      { value: 'correct', label: 'Correct Choice' },
      { value: 'aggressive', label: 'Aggressive (Reasonable)' },
      { value: 'held', label: 'Held Ball (Should Have Thrown)' },
      { value: 'forced', label: 'Forced / Rushed' },
    ] as { value: FieldingDecision; label: string }[],

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    decisionWeightClasses: {
      not_required: 'na',
      correct: 'clean',
      aggressive: 'clean', // reasonable aggression that didn't pan out isn't penalized — that's the point of the label
      forced: 'minor_miss',
      wrong_base: 'major_miss',
      held: 'major_miss', // a catcher sitting on the ball when a throw was makeable costs an out same as wrong_base
    } as Record<string, string>,
    primaryPitchQualityStates: [
      { label: '★ Missed', value: 1 },
      { label: '★★ On Target', value: 2 },
      { label: '★★★ Unhittable', value: 3 },
    ],
    primaryHitQualityStates: [
      { label: '★ Contact', value: 1 },
      { label: '★★ Solid', value: 2 },
      { label: '★★★ Ripped', value: 3 },
    ],
    primaryPlayQualityStates: [
      { label: '★ Routine', value: 1 },
      { label: '★★ Tough', value: 2 },
      { label: '★★★ Gem', value: 3 },
    ],
    primaryPlayErrorStates: [
      { label: 'No Official Error', value: 'none' },
      { label: 'Fielding Error', value: 'fielding' },
      { label: 'Throwing Error', value: 'throwing' },
    ],

    hitOutcomeRules: [
      { label: '1B', value: 'S', startBase: 'B', endBase: '1', isOut: false, runsScored: 0 },
      { label: '2B', value: 'D', startBase: 'B', endBase: '2', isOut: false, runsScored: 0 },
      { label: '3B', value: 'T', startBase: 'B', endBase: '3', isOut: false, runsScored: 0 },
      { label: 'HR', value: 'HR', startBase: 'B', endBase: 'H', isOut: false, runsScored: 1 },
      { label: 'FC', value: 'FC', startBase: 'B', endBase: '1', isOut: false, runsScored: 0 }, // Batter usually reaches 1st safely on FC
    ] as PlayRule[],

    pitchOutcomeRules: [
      { label: 'Walk', value: 'BB', startBase: 'B', endBase: '1', isOut: false, runsScored: 0 },
      {
        label: 'Intentional Walk',
        value: 'IBB',
        startBase: 'B',
        endBase: '1',
        isOut: false,
        runsScored: 0,
      },
      {
        label: 'Hit By Pitch',
        value: 'HP',
        startBase: 'B',
        endBase: '1',
        isOut: false,
        runsScored: 0,
      },
    ] as PlayRule[],

    trajectoryOptions: [
      { label: 'Grounder (G)', value: 'G' },
      { label: 'Line Drive (L)', value: 'L' },
      { label: 'Fly Ball (F)', value: 'F' },
      { label: 'Pop-up (P)', value: 'P' },
    ],
    battedBallInAirTrajectories: ['F', 'L', 'P'],

    // state
    primaryRunnerActions: [
      { label: 'Out', value: 'Out', color: 'negative', visible: true },
      { label: 'Score', value: 'Score', color: 'positive', visible: true },
    ] as ActionButtonDef[],

    primaryRunnerAdvanceReasonsSimplified: [
      { value: 'SB', label: 'Stolen Base' },
      { value: 'WP', label: 'Wild Pitch' },
      { value: 'PB', label: 'Passed Ball' },
      { value: 'DEFENSE', label: 'Defense' },
      { value: 'OTHER', label: 'Other' },
    ] as { value: RunnerAdvanceReason; label: string }[],

    primaryDefenseSubReasons: [
      { value: 'error', label: 'Error' },
      { value: 'throw', label: 'Throw' },
      { value: 'pickoff', label: 'Pickoff' },
      { value: 'caught_stealing', label: 'Caught Stealing' },
    ] as { value: DefenseSubReason; label: string }[],

    primaryBaserunningJumpOptions: [
      { value: 'routine', label: 'Routine' },
      { value: 'quick', label: 'Quick' },
      { value: 'late', label: 'Late' },
      { value: 'walking_lead', label: 'Walking Lead' },
    ] as { value: BaserunningJump; label: string }[],
    primaryBaserunningDecisionOptions: [
      { value: 'not_required', label: 'N/A' },
      { value: 'correct', label: 'Correct' },
      { value: 'aggressive', label: 'Aggressive (Reasonable)' },
      { value: 'hesitant', label: 'Hesitant' },
      { value: 'forced', label: 'Forced / Bad Read' },
    ] as { value: BaserunningDecision; label: string }[],
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    baserunningJumpWeightClasses: {
      routine: 'routine',
      walking_lead: 'routine',
      quick: 'positive',
      late: 'minor_miss',
    } as Record<string, string>,

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    baserunningDecisionWeightClasses: {
      not_required: 'na',
      correct: 'clean',
      aggressive: 'clean',
      hesitant: 'minor_miss',
      forced: 'major_miss',
    } as Record<string, string>,
  }),

  getters: {
    getPositionShortLabel: (state): ((pos: number) => string) => {
      return (pos: number): string => {
        const str = state.positionStrings[pos]
        return str ? `${pos}- ${str}` : String(pos)
      }
    },
    useDesignatedHitter: () => {
      const auth = useAuthStore()
      // Defaults to true for standard baseball/softball play, unless explicitly disabled
      return auth.currentUser?.settings?.useDesignatedHitter !== false
    },

    hitOutcomeToggleOptions(state): { label: string; value: string }[] {
      return [
        ...state.hitOutcomeRules.map((r) => ({ label: r.label, value: r.value })),
        { label: 'Other...', value: 'OTHER' },
        { label: 'Undo', value: 'UNDO' },
      ]
    },

    playPrimaryOutcomesValues: (state): string[] =>
      state.primaryPlayOutcomes.map((option) => option.value),
    playAdvancedOutcomesValues: (state): string[] =>
      state.playAdvancedOutcomes.map((option) => option.value),

    pitchAdvancedOptions: (state): { label: string; value: string }[] => {
      const auth = useAuthStore()
      const hidden = auth.currentUser?.settings?.hiddenPitchOptions || []
      return state._rawPitchAdvancedOptions.filter((opt) => !hidden.includes(opt.value))
    },

    pitchAdvancedOptionsValues: (state): string[] =>
      state._rawPitchAdvancedOptions.map((option) => option.value),

    runnerAdvanceOptions: (
      state,
    ): { label: string; value: string; default_color: string; select_color: string }[] => {
      const auth = useAuthStore()
      const hidden = auth.currentUser?.settings?.hiddenRunnerOptions || []
      return state._rawRunnerAdvanceOptions.filter((opt) => !hidden.includes(opt.value))
    },

    defaultInnings: (): number => {
      const auth = useAuthStore()
      return auth.currentUser?.settings?.defaultInnings || 9
    },

    defaultRosterSize: (): number => {
      const auth = useAuthStore()
      return auth.currentUser?.settings?.defaultRosterSize || 12
    },

    // =========================================================
    // 1. FLATTENED RAW ARRAYS FOR USER SETTINGS LOOP
    // =========================================================
    _rawApproachOptions: (state): { value: FieldingApproach; label: string }[] => {
      const all = [
        ...state.primaryApproachOptionsInfield,
        ...state.primaryApproachOptionsOutfield,
        ...state.primaryApproachOptionsCatcher,
        ...state.primaryApproachOptionsPitcher,
        ...state.primaryApproachOptionsFirstBase,
        ...state.primaryApproachOptionsRelay,
      ]
      return Array.from(new Map(all.map((item) => [item.value, item])).values())
    },
    _rawExecutionOptions: (state): { value: FieldingExecution; label: string }[] => {
      const all = [
        ...state.primaryExecutionOptionsInfield,
        ...state.primaryExecutionOptionsOutfield,
        ...state.primaryExecutionOptionsCatcher,
        ...state.primaryExecutionOptionsPitcher,
        ...state.primaryExecutionOptionsFirstBase,
        ...state.primaryExecutionOptionsRelay,
      ]
      return Array.from(new Map(all.map((item) => [item.value, item])).values())
    },
    _rawTransferOptions: (state): { value: FieldingTransferQuality; label: string }[] => {
      const all = [
        ...state.primaryTransferOptionsInfield,
        ...state.primaryTransferOptionsOutfield,
        ...state.primaryTransferOptionsCatcher,
        ...state.primaryTransferOptionsPitcher,
        ...state.primaryTransferOptionsRelay,
      ]
      return Array.from(new Map(all.map((item) => [item.value, item])).values())
    },
    _rawThrowOptions: (state): { value: FieldingThrowQuality; label: string }[] => {
      const all = [
        ...state.primaryThrowOptionsInfield,
        ...state.primaryThrowOptionsOutfield,
        ...state.primaryThrowOptionsCatcher,
        ...state.primaryThrowOptionsPitcher,
        ...state.primaryThrowOptionsFirstBase,
        ...state.primaryThrowOptionsRelay,
      ]
      return Array.from(new Map(all.map((item) => [item.value, item])).values())
    },
    _rawQualityOptions: (state): { label: string; value: number }[] => {
      return state.primaryPlayQualityStates
    },

    // =========================================================
    // 2. CONTEXT & USER SETTING AWARE GETTERS FOR ACTION HUD
    // =========================================================
    approachOptionsForPosition: (state) => {
      return (position: number, role: string, trajectory?: string) => {
        const auth = useAuthStore()
        const settings = auth.currentUser?.settings
        if (settings?.hiddenDimensions?.includes('approach')) return []

        const situation = resolveFieldingSituation(position, role, trajectory)
        const groupMap: Record<string, typeof state.primaryApproachOptionsGroundBall> = {
          ground_ball: state.primaryApproachOptionsGroundBall,
          air_ball: state.primaryApproachOptionsAirBall,
          receiving: state.primaryApproachOptionsReceiving,
          relay_decision: state.primaryApproachOptionsRelayDecision,
          catcher_blocking: state.primaryApproachOptionsCatcher,
        }
        const raw = groupMap[situation] || state.primaryApproachOptionsGroundBall
        const hidden = settings?.hiddenApproachOptions || []
        const customLabels = settings?.customLabels || {}

        return raw
          .filter((opt) => !hidden.includes(opt.value))
          .map((opt) => ({ ...opt, label: customLabels[opt.value] || opt.label }))
      }
    },

    executionOptionsForPosition: (state) => {
      return (position: number, role: string, trajectory?: string) => {
        const auth = useAuthStore()
        const settings = auth.currentUser?.settings
        if (settings?.hiddenDimensions?.includes('execution')) return []

        const situation = resolveFieldingSituation(position, role, trajectory)
        const groupMap: Record<string, typeof state.primaryExecutionOptionsGroundBall> = {
          ground_ball: state.primaryExecutionOptionsGroundBall,
          air_ball: state.primaryExecutionOptionsAirBall,
          receiving: state.primaryExecutionOptionsReceiving,
          relay_decision: state.primaryExecutionOptionsRelayDecision,
          catcher_blocking: state.primaryExecutionOptionsCatcher,
        }
        const raw = groupMap[situation] || state.primaryExecutionOptionsGroundBall
        const hidden = settings?.hiddenExecutionOptions || []
        const customLabels = settings?.customLabels || {}

        return raw
          .filter((opt) => !hidden.includes(opt.value))
          .map((opt) => ({ ...opt, label: customLabels[opt.value] || opt.label }))
      }
    },

    transferOptionsForPosition: (
      state,
    ): ((
      position: number,
      role: string,
    ) => { value: FieldingTransferQuality; label: string; color?: string; weight?: number }[]) => {
      return (position: number, role: string) => {
        const auth = useAuthStore()
        const settings = auth.currentUser?.settings

        if (settings?.hiddenDimensions?.includes('transfer')) return []

        const group = resolveFieldingGroup(position, role)
        const groupMap: Record<string, typeof state.primaryTransferOptionsInfield> = {
          infield: state.primaryTransferOptionsInfield,
          outfield: state.primaryTransferOptionsOutfield,
          catcher: state.primaryTransferOptionsCatcher,
          pitcher: state.primaryTransferOptionsPitcher,
          firstbase: state.primaryTransferOptionsInfield,
          relay: state.primaryTransferOptionsRelay,
        }
        const raw = groupMap[group] || state.primaryTransferOptionsInfield
        const hidden = settings?.hiddenTransferOptions || []
        const customLabels = settings?.customLabels || {}

        const weights: Record<string, number> = { na: 0, clean: 0, minor_miss: -1, major_miss: -2 }

        return raw
          .filter((opt) => !hidden.includes(opt.value))
          .map((opt) => {
            const weightClass = state.transferWeightClasses[opt.value] ?? 'clean'
            const weight = weights[weightClass] ?? 0
            const baseLabel = customLabels[opt.value] || opt.label
            const graded = formatGradedOption(baseLabel, weight)

            return { ...opt, label: graded.label, color: graded.color, weight: graded.weight }
          })
      }
    },

    throwOptionsForPosition: (state) => {
      return (position: number, role: string) => {
        const auth = useAuthStore()
        const settings = auth.currentUser?.settings
        if (settings?.hiddenDimensions?.includes('throw')) return []

        const group = resolveThrowDecisionGroup(position, role)
        const groupMap: Record<string, typeof state.primaryThrowOptionsFielding> = {
          fielding: state.primaryThrowOptionsFielding,
          receiving: state.primaryThrowOptionsReceiving,
          catcher: state.primaryThrowOptionsCatcher,
          relay: state.primaryThrowOptionsRelay,
        }
        const raw = groupMap[group] || state.primaryThrowOptionsFielding
        const hidden = settings?.hiddenThrowOptions || []
        const customLabels = settings?.customLabels || {}

        return raw
          .filter((opt) => !hidden.includes(opt.value))
          .map((opt) => ({ ...opt, label: customLabels[opt.value] || opt.label }))
      }
    },

    decisionOptionsForPosition: (state) => {
      return (position: number, role: string) => {
        const auth = useAuthStore()
        const settings = auth.currentUser?.settings
        if (settings?.hiddenDimensions?.includes('decision')) return []

        const group = resolveThrowDecisionGroup(position, role)
        const groupMap: Record<string, typeof state.primaryDecisionOptionsGeneral> = {
          fielding: state.primaryDecisionOptionsGeneral,
          receiving: state.primaryDecisionOptionsGeneral,
          relay: state.primaryDecisionOptionsGeneral,
          catcher: state.primaryDecisionOptionsCatcher,
        }
        const raw = groupMap[group] || state.primaryDecisionOptionsGeneral
        const hidden = settings?.hiddenDecisionOptions || []
        const customLabels = settings?.customLabels || {}

        return raw
          .filter((opt) => !hidden.includes(opt.value))
          .map((opt) => ({ ...opt, label: customLabels[opt.value] || opt.label }))
      }
    },

    playQualityOptions: (state): { label: string; value: number }[] => {
      const auth = useAuthStore()
      const settings = auth.currentUser?.settings

      if (settings?.hiddenDimensions?.includes('quality')) return []

      const hidden = settings?.hiddenQualityOptions || []
      const customLabels = settings?.customLabels || {}

      return state.primaryPlayQualityStates
        .filter((opt) => !hidden.includes(String(opt.value)))
        .map((opt) => ({ ...opt, label: customLabels[String(opt.value)] || opt.label }))
    },

    activeScoutingTags(state): ScoutingTagDef[] {
      const auth = useAuthStore()
      const settings = auth.currentUser?.settings

      if (settings?.hiddenDimensions?.includes('scouting')) return []

      const hidden = settings?.hiddenScoutingTags || []
      const customLabels = settings?.customLabels || {}
      const customCategories = settings?.customCategories || {}

      const validCategories = ['positive', 'negative', 'neutral'] as const
      type ScoutingCategory = (typeof validCategories)[number]

      const isValidCategory = (val: unknown): val is ScoutingCategory =>
        typeof val === 'string' && (validCategories as readonly string[]).includes(val)

      return state._rawScoutingTags
        .filter((opt) => !hidden.includes(opt.value))
        .map((opt): ScoutingTagDef => {
          const override = customCategories[opt.value]
          const category: ScoutingCategory = isValidCategory(override) ? override : opt.category

          let color = 'grey-7'
          if (category === 'positive') color = 'positive'
          else if (category === 'negative') color = 'negative'
          else if (category === 'neutral') color = 'info'

          return {
            ...opt,
            label: customLabels[opt.value] || opt.label,
            category,
            color,
          }
        })
    },
  },

  actions: {
    // Utility function: Pass in 'S' and get back the rule dictating the batter goes to '1'
    getRuleForOutcome(outcomeValue: string): PlayRule | undefined {
      return this.hitOutcomeRules.find((r) => r.value === outcomeValue)
    },
    getPitchRuleForOutcome(outcomeValue: string): PlayRule | undefined {
      return this.pitchOutcomeRules.find((r) => r.value === outcomeValue)
    },
    getApproachWeight(value: string): number {
      const weightClass = this.approachWeightClasses[value] ?? 'routine'
      const weights: Record<string, number> = {
        unscored: 0,
        routine: 0,
        lateral: 1,
        aggressive: 1,
        extreme: 2,
      }
      return weights[weightClass] ?? 0
    },
    getExecutionWeight(value: string): number {
      const weightClass = this.executionWeightClasses[value] ?? 'clean'
      const weights: Record<string, number> = {
        unscored: 0,
        clean: 0,
        external: 0,
        minor_miss: -1,
        major_miss: -2,
      }
      return weights[weightClass] ?? 0
    },
    getTransferWeight(value: string): number {
      const weightClass = this.transferWeightClasses[value] ?? 'clean'
      const weights: Record<string, number> = { na: 0, clean: 0, minor_miss: -1, major_miss: -2 }
      return weights[weightClass] ?? 0
    },
    getThrowWeight(value: string): number {
      const weightClass = this.throwWeightClasses[value] ?? 'clean'
      const weights: Record<string, number> = { na: 0, clean: 0, minor_miss: -1, major_miss: -2 }
      return weights[weightClass] ?? 0
    },
    getDecisionWeight(value: string): number {
      const weightClass = this.decisionWeightClasses[value] ?? 'clean'
      const weights: Record<string, number> = { na: 0, clean: 0, minor_miss: -1, major_miss: -2 }
      return weights[weightClass] ?? 0
    },
    getBaserunningJumpWeight(value: string): number {
      const weightClass = this.baserunningJumpWeightClasses[value] ?? 'routine'
      const weights: Record<string, number> = { routine: 0, positive: 1, minor_miss: -1 }
      return weights[weightClass] ?? 0
    },

    getBaserunningDecisionWeight(value: string): number {
      const weightClass = this.baserunningDecisionWeightClasses[value] ?? 'clean'
      const weights: Record<string, number> = { na: 0, clean: 0, minor_miss: -1, major_miss: -2 }
      return weights[weightClass] ?? 0
    },
    getPitchPrimaryCategory(pitchOutcome: string): string | null {
      if (['B', 'W', 'H', 'I', 'V'].includes(pitchOutcome)) return 'B'
      // Added 'A' (Auto Strike), 'N' (No Pitch), 'U' (Unknown)
      if (['C', 'S', 'F', 'T', 'K', 'L', 'M', 'O', 'Q', 'R', 'A', 'N', 'U'].includes(pitchOutcome))
        return 'S'
      if (['X', 'Y'].includes(pitchOutcome)) return 'X'
      else return null
    },
    getHitCheckAndAdvanceView(hitOutcome: string): string {
      if (['S', 'D', 'T', 'HR', 'OUT', 'FC', 'E'].includes(hitOutcome)) {
        return 'play'
      } else if (hitOutcome === 'OUT_Q') {
        return 'pitch'
      } else {
        return ''
      }
    },
  },
})
